/**
 * ToolsHub — "Tools" top nav for the Athletic Director + Scheduler (AJ,
 * 2026-10-12): Scorebook Log · Scorebook Stickers · Season Program.
 * The Scorebook tabs preview the REAL print PDF (what you see is what prints),
 * then Download or Email a link. Backend routes/scorebook.py; doc docs/SCOREBOOK.md.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Tags, FileText, Download, Send, Loader2, IdCard } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/scorebook`;
const TABS = [
  { key: 'log', label: 'Scorebook Log', icon: BookOpen },
  { key: 'stickers', label: 'Scorebook Stickers', icon: Tags },
  { key: 'program', label: 'Season Program', icon: FileText },
  { key: 'pass', label: 'Player Pass', icon: IdCard },
];
const inp = 'h-7 text-xs rounded border border-slate-300 px-2 bg-white text-slate-900';

function ScorebookPanel({ orgId, kind }) {
  const [sports, setSports] = useState([]);
  const [sport, setSport] = useState('');
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState({});
  const [covers, setCovers] = useState(true);
  const [strips, setStrips] = useState(0);   // 0 = fill the page
  const [template, setTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [fill, setFill] = useState(true);
  const [booklet, setBooklet] = useState(true);
  const [pdfUrl, setPdfUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [emails, setEmails] = useState('');
  const [pcfg, setPcfg] = useState(null);      // Season Program settings
  const [groups, setGroups] = useState([]);
  const [rev, setRev] = useState(0);           // bump to rebuild the preview after a settings save

  useEffect(() => {
    if (kind !== 'stickers' || !orgId) return;
    axios.get(`${API}/${orgId}/templates`).then((r) => { setTemplates(r.data.templates); setTemplate(r.data.default); }).catch(() => {});
  }, [orgId, kind]);
  useEffect(() => {
    if (kind !== 'program' || !orgId) return;
    axios.get(`${API}/${orgId}/program-config`).then((r) => { setPcfg(r.data.config); setGroups(r.data.staff_groups); }).catch(() => {});
  }, [orgId, kind]);
  const savePcfg = async (patch) => {
    try { const r = await axios.put(`${API}/${orgId}/program-config`, patch); setPcfg(r.data.config); setRev((v) => v + 1); }
    catch { toast.error('Could not save program settings'); }
  };

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/${orgId}/sports`).then((r) => { setSports(r.data.sports); setSport((s) => s || r.data.sports[0]?.key || ''); })
      .catch(() => toast.error('Could not load sports'));
  }, [orgId]);
  useEffect(() => {
    if (!orgId || !sport) return;
    setData(null);
    axios.get(`${API}/${orgId}/${sport}`).then((r) => {
      // Stickers are usually printed for ONE team -> start with just the first team checked.
      setData(r.data); setPicked(Object.fromEntries(r.data.teams.map((t, i) => [t.slug, kind !== 'stickers' || i === 0])));
    }).catch(() => setData({ teams: [] }));
  }, [orgId, sport, kind]);

  // Only build once THIS sport's teams have loaded (avoids a request that mixes
  // the new sport with the previous sport's team picks).
  const ready = !!data && data.sport === sport && (kind !== 'stickers' || !!template);
  const teamsParam = ready ? data.teams.filter((t) => picked[t.slug]).map((t) => t.slug).join(',') : '';
  const query = `teams=${encodeURIComponent(teamsParam)}&covers=${covers ? 1 : 0}&strips=${strips}&template=${template}&fill=${fill ? 1 : 0}&booklet=${booklet ? 1 : 0}&rev=${rev}`;

  const render = useCallback(async () => {
    if (!ready) return;
    if (!teamsParam) { setPdfUrl(''); return; }
    setBusy(true);
    try {
      const r = await axios.get(`${API}/${orgId}/${sport}/${kind}.pdf?${query}`, { responseType: 'blob' });
      setPdfUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); });
    } catch { toast.error('Could not build the PDF'); } finally { setBusy(false); }
  }, [orgId, sport, kind, query, teamsParam, ready]);
  useEffect(() => { render(); }, [render]);

  const download = () => {
    const a = document.createElement('a');
    const nm = { log: 'Scorebook_Log', stickers: 'Scorebook_Stickers', program: 'Season_Program', pass: 'Player_Pass' }[kind];
    a.href = pdfUrl; a.download = `${nm}_${sport}_${data?.season_label || ''}.pdf`; a.click();
  };
  const email = async () => {
    try {
      const r = await axios.post(`${API}/${orgId}/${sport}/email`, {
        kind, to: emails.split(',').map((e) => e.trim()).filter(Boolean), teams: teamsParam, covers, strips, template, fill, booklet });
      toast.success(`Emailed a link to ${r.data.sent.length} recipient(s)`); setEmails('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
  };

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
        <div className="inline-flex gap-1">
          {sports.map((s) => (
            <Button key={s.key} size="sm" variant="ghost" onClick={() => setSport(s.key)}
              className={`h-7 px-3 text-xs border ${sport === s.key ? 'bg-teal-50 text-teal-700 border-teal-200' : 'border-transparent text-slate-500'}`}
              data-testid={`sb-sport-${s.key}`}>{s.name}</Button>
          ))}
        </div>
        {data?.season_label && <span className="text-xs text-slate-500">{data.season_label} season</span>}
        {kind === 'program' && (
          <select className={inp} value={booklet ? 'b' : 'p'} onChange={(e) => setBooklet(e.target.value === 'b')} data-testid="sp-booklet">
            <option value="b">Booklet for printing (fold)</option><option value="p">Pages in reading order</option></select>
        )}
        {kind === 'log' && <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={covers} onChange={(e) => setCovers(e.target.checked)} />Cover labels</label>}
        {(kind === 'log' || kind === 'pass') && (
          <select className={inp} value={fill ? 'f' : 'o'} onChange={(e) => setFill(e.target.value === 'f')} data-testid="sl-fill">
            <option value="f">Fill page</option><option value="o">One per team</option></select>
        )}
        {kind === 'stickers' && (
          <>
            <label className="flex items-center gap-1 text-xs text-slate-600">Copies per team
              <select className={inp} value={strips} onChange={(e) => setStrips(Number(e.target.value))}>
                <option value={0}>Fill page</option>{[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
            <label className="flex items-center gap-1 text-xs text-slate-600">Scorebook
              <select className={inp} value={template} onChange={(e) => setTemplate(e.target.value)} data-testid="ss-template">
                {templates.map((t) => <option key={t.id} value={t.id} disabled={!t.ready}>{t.label}</option>)}</select></label>
          </>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!pdfUrl} onClick={download} data-testid="sb-download"><Download className="h-3 w-3 mr-1" />Download PDF</Button>
          <input className={`${inp} w-56`} placeholder="Email to (comma separated)" value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="sb-email-to" />
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!emails.trim() || !teamsParam} onClick={email} data-testid="sb-email"><Send className="h-3 w-3 mr-1" />Email</Button>
        </div>
      </div>
      {kind === 'program' && pcfg && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600" data-testid="sp-config">
          <label>Cover title
            <input className={`${inp} w-full`} defaultValue={pcfg.cover_title} onBlur={(e) => e.target.value !== pcfg.cover_title && savePcfg({ cover_title: e.target.value })} /></label>
          <label className="lg:col-span-2">Cover message (e.g. team verse)
            <textarea className="w-full text-xs rounded border border-slate-300 p-1 bg-white text-slate-900" rows={2} defaultValue={pcfg.cover_text}
              onBlur={(e) => e.target.value !== pcfg.cover_text && savePcfg({ cover_text: e.target.value })} data-testid="sp-cover-text" /></label>
          <label>Closing leadership line
            <select className={`${inp} w-full`} value={pcfg.leadership_group_id || ''} onChange={(e) => savePcfg({ leadership_group_id: e.target.value })}>
              <option value="">Athletic Director only</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name} (all members)</option>)}</select></label>
          <label className="flex items-center gap-1 self-end"><input type="checkbox" checked={pcfg.include_parents} onChange={(e) => savePcfg({ include_parents: e.target.checked })} />Show parents column</label>
          <p className="self-end text-[11px] text-slate-400">Team photos: WP Media ▸ team photo for this season (else the evergreen photo). Sponsors: active for this sport + season.</p>
        </div>
      )}
      {data && (
        <div className="flex flex-wrap gap-3 text-xs text-slate-700">
          {data.teams.length === 0 && <span className="text-slate-500">No current-season roster teams with players for this sport.</span>}
          {data.teams.map((t) => (
            <label key={t.slug} className="flex items-center gap-1">
              <input type="checkbox" checked={!!picked[t.slug]} onChange={(e) => setPicked({ ...picked, [t.slug]: e.target.checked })} />
              {t.code} <span className="text-slate-400">({t.players.length}{t.coach ? '' : ', no coach'})</span>
            </label>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-500">
        {{ log: covers ? <>Print in <b>color</b>, <b>double-sided, flip on short edge</b>, at <b>100% / Actual size</b> — each cover lands on the back of its roster card.</> : <>Print in <b>color</b>, one-sided, at <b>100% / Actual size</b>.</>,
           stickers: <>Print <b>one-sided</b> at <b>100% / Actual size</b> on the label sheet. The frame and dotted lines are on-screen guides only — they do not print.</>,
           pass: <>Print in <b>color</b>, <b>double-sided, flip on LONG edge</b>, at <b>100% / Actual size</b> — each contact back lands behind its own team's pass.</>,
           program: booklet ? <>Print <b>double-sided, flip on short edge</b>, at <b>100% / Actual size</b>, then fold the stack in half.</> : <>Reading-order pages for screen or email — use “Booklet” to print.</> }[kind]}
        {' '}These settings are saved in the PDF (Adobe Reader applies them automatically; in Chrome/Edge pick them in the print dialog).
      </p>
      <div className="rounded-lg border border-slate-200 bg-slate-100" style={{ height: '75vh' }}>
        {busy ? <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          : pdfUrl ? <iframe title={`${kind} preview`} src={pdfUrl} className="w-full h-full rounded-lg" data-testid="sb-preview" />
            : <div className="flex h-full items-center justify-center text-slate-500">Pick at least one team.</div>}
      </div>
    </div>
  );
}

export default function ToolsHub({ orgId }) {
  const [tab, setTab] = useState('log');
  return (
    <div className="space-y-4" data-testid="tools-hub">
      <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
        {TABS.map((t) => {
          const Icon = t.icon; const active = tab === t.key;
          return (
            <Button key={t.key} variant="ghost" size="sm" onClick={() => setTab(t.key)}
              className={`h-8 px-3 text-xs font-medium border ${active ? 'bg-teal-50 text-teal-700 border-teal-200' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
              data-testid={`tools-tab-${t.key}`}><Icon className="h-3.5 w-3.5 mr-1.5" />{t.label}</Button>
          );
        })}
      </div>
      {tab === 'log' && <ScorebookPanel key="log" orgId={orgId} kind="log" />}
      {tab === 'stickers' && <ScorebookPanel key="stickers" orgId={orgId} kind="stickers" />}
      {tab === 'program' && <ScorebookPanel key="program" orgId={orgId} kind="program" />}
      {tab === 'pass' && <ScorebookPanel key="pass" orgId={orgId} kind="pass" />}
    </div>
  );
}
